import { useState, useCallback } from "react";
import { useSearchPlayersQuery } from "../store/api/mlbApi";
import { useDebounce } from "./useDebounce";
import {
  COMPARE_MAX_PLAYERS,
  assignIdToSlots,
} from "../components/compare/compareShell";

/**
 * Custom hook for Compare page player search functionality
 * Provides enhanced search with multi-word matching while preserving
 * the existing quick selection and multi-slot logic
 */
export const useComparePlayerSearch = (maxSlots = COMPARE_MAX_PLAYERS) => {
  // State for each player slot - start with 2 players
  const [playerSlots, setPlayerSlots] = useState(
    Array.from({ length: 2 }, () => ({
      id: null,
      data: null,
      searchTerm: "",
      inputValue: "",
    }))
  );

  // Search queries for each slot (call hooks at top level)
  const searchQuery1 = useSearchPlayersQuery(
    playerSlots[0]?.searchTerm.length >= 2 ? playerSlots[0].searchTerm : null,
    { skip: !playerSlots[0] || playerSlots[0].searchTerm.length < 2 }
  );
  const searchQuery2 = useSearchPlayersQuery(
    playerSlots[1]?.searchTerm.length >= 2 ? playerSlots[1].searchTerm : null,
    { skip: !playerSlots[1] || playerSlots[1].searchTerm.length < 2 }
  );
  const searchQuery3 = useSearchPlayersQuery(
    playerSlots[2]?.searchTerm.length >= 2 ? playerSlots[2].searchTerm : null,
    { skip: !playerSlots[2] || playerSlots[2].searchTerm.length < 2 }
  );
  const searchQuery4 = useSearchPlayersQuery(
    playerSlots[3]?.searchTerm.length >= 2 ? playerSlots[3].searchTerm : null,
    { skip: !playerSlots[3] || playerSlots[3].searchTerm.length < 2 }
  );
  const searchQuery5 = useSearchPlayersQuery(
    playerSlots[4]?.searchTerm.length >= 2 ? playerSlots[4].searchTerm : null,
    { skip: !playerSlots[4] || playerSlots[4].searchTerm.length < 2 }
  );

  // Create debounced search functions for each slot (call hooks at top level)
  const debouncedSearch1 = useDebounce((searchTerm) => {
    setPlayerSlots((prev) =>
      prev.map((slot, i) => (i === 0 ? { ...slot, searchTerm } : slot))
    );
  }, 300);

  const debouncedSearch2 = useDebounce((searchTerm) => {
    setPlayerSlots((prev) =>
      prev.map((slot, i) => (i === 1 ? { ...slot, searchTerm } : slot))
    );
  }, 300);

  const debouncedSearch3 = useDebounce((searchTerm) => {
    setPlayerSlots((prev) =>
      prev.map((slot, i) => (i === 2 ? { ...slot, searchTerm } : slot))
    );
  }, 300);

  const debouncedSearch4 = useDebounce((searchTerm) => {
    setPlayerSlots((prev) =>
      prev.map((slot, i) => (i === 3 ? { ...slot, searchTerm } : slot))
    );
  }, 300);

  const debouncedSearch5 = useDebounce((searchTerm) => {
    setPlayerSlots((prev) =>
      prev.map((slot, i) => (i === 4 ? { ...slot, searchTerm } : slot))
    );
  }, 300);

  // Handle search input changes
  const handleSearchInputChange = useCallback(
    (index, newInputValue) => {
      // Update input value immediately for responsive UI
      setPlayerSlots((prev) =>
        prev.map((slot, i) =>
          i === index ? { ...slot, inputValue: newInputValue } : slot
        )
      );

      // Debounce the search term update
      if (index === 0) {
        debouncedSearch1(newInputValue);
      } else if (index === 1) {
        debouncedSearch2(newInputValue);
      } else if (index === 2) {
        debouncedSearch3(newInputValue);
      } else if (index === 3) {
        debouncedSearch4(newInputValue);
      } else if (index === 4) {
        debouncedSearch5(newInputValue);
      }
    },
    [
      debouncedSearch1,
      debouncedSearch2,
      debouncedSearch3,
      debouncedSearch4,
      debouncedSearch5,
    ]
  );

  // Handle player selection from search
  const handlePlayerSelect = useCallback((index, newValue) => {
    if (!newValue) {
      // Clear the slot when no value is selected (X button clicked)
      setPlayerSlots((prev) =>
        prev.map((slot, i) =>
          i === index
            ? {
                ...slot,
                id: null,
                data: null,
                searchTerm: "",
                inputValue: "",
              }
            : slot
        )
      );
      return;
    }

    setPlayerSlots((prev) =>
      prev.map((slot, i) =>
        i === index
          ? {
              ...slot,
              id: newValue.playerId,
              data: null, // Will be populated by the query
              searchTerm: "", // Clear search term
              inputValue: "", // Clear input value
            }
          : slot
      )
    );
  }, []);

  const handlePopularPlayerSelect = useCallback(
    (selectedPlayer) => {
      const playerId = selectedPlayer?.playerId;
      if (!playerId) return;

      setPlayerSlots((prev) => {
        const nextIds = assignIdToSlots(
          prev.map((slot) => slot.id),
          playerId,
          maxSlots
        );
        return nextIds.map((id, index) => {
          if (prev[index] && prev[index].id === id) return prev[index];
          if (!id) {
            return { id: null, data: null, searchTerm: "", inputValue: "" };
          }
          return { id, data: null, searchTerm: "", inputValue: "" };
        });
      });
    },
    [maxSlots]
  );

  // Add a new player slot
  const handleAddPlayer = useCallback(() => {
    setPlayerSlots((prev) => [
      ...prev,
      { id: null, data: null, searchTerm: "", inputValue: "" },
    ]);
  }, []);

  // Remove a player slot
  const handleRemovePlayer = useCallback((index) => {
    setPlayerSlots((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Update player data when queries complete
  const updatePlayerData = useCallback((index, playerData) => {
    setPlayerSlots((prev) =>
      prev.map((slot, i) =>
        i === index ? { ...slot, data: playerData } : slot
      )
    );
  }, []);

  // Get search query for a specific slot
  const getSearchQueryForSlot = useCallback(
    (index) => {
      switch (index) {
        case 0:
          return searchQuery1;
        case 1:
          return searchQuery2;
        case 2:
          return searchQuery3;
        case 3:
          return searchQuery4;
        case 4:
          return searchQuery5;
        default:
          return { data: [], isLoading: false, error: null };
      }
    },
    [searchQuery1, searchQuery2, searchQuery3, searchQuery4, searchQuery5]
  );

  // Get player slot data
  const getPlayerSlot = useCallback(
    (index) => {
      return (
        playerSlots[index] || {
          id: null,
          data: null,
          searchTerm: "",
          inputValue: "",
        }
      );
    },
    [playerSlots]
  );

  const setPlayerIds = useCallback((playerIds) => {
    const ids = playerIds.filter(Boolean).slice(0, maxSlots);
    if (ids.length === 0) return;

    setPlayerSlots(
      ids.map((id) => ({
        id,
        data: null,
        searchTerm: "",
        inputValue: "",
      }))
    );
  }, [maxSlots]);

  return {
    playerSlots,
    handleSearchInputChange,
    handlePlayerSelect,
    handlePopularPlayerSelect,
    handleAddPlayer,
    handleRemovePlayer,
    updatePlayerData,
    getSearchQueryForSlot,
    getPlayerSlot,
    setPlayerIds,
  };
};
